import type { ChangeEvent, FocusEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { cn } from '@crossnokaye/ux-common';

import { resolveSourceUnits } from '@/features/equipment-browser/utils';
import { getCatalogUnitsLabel } from '@/helpers/unit-formatter/get-catalog-units-label';
import { transformUnits } from '@/helpers/unit-formatter/transform-units';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { Input, InputField, InputUnit } from '@/ux-common/components/input/input';

interface EquipmentBulkEditTableInputCellProps {
  columnId: string;
  rowId: string;
  displayUnits?: string;
  units?: string;
  disabled?: boolean;
}

/**
 * Cell that displays a setting value (metrics, temperature, etc.).
 * Binds to React Hook Form context using the path `values.{rowId}.{columnId}`.
 * Converts from source units to display units for display, and converts
 * back from display units to source units on change.
 *
 * Uses local state during editing to allow intermediate values like trailing
 * decimal points (e.g. "1.") that would otherwise be stripped by formatUnits.
 * The form value is updated with the converted source units on blur.
 */
export const EquipmentBulkEditTableInputCell = ({
  columnId,
  rowId,
  displayUnits,
  units,
  disabled,
}: EquipmentBulkEditTableInputCellProps): ReactNode => {
  const { control, getFieldState } = useFormContext();
  const fieldName = `values.${rowId}.${columnId}`;
  const { isDirty } = getFieldState(fieldName);
  const { field } = useController({
    control,
    name: fieldName,
  });

  const sourceUnits = resolveSourceUnits(displayUnits, units);

  // Track whether the input is focused so we can show raw typed text
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const seededDisplayRef = useRef('');

  // For editable inputs, empty/null values should show an empty input (not "--")
  const isValueEmpty = field.value == null || field.value === '';
  const displayValue = isValueEmpty
    ? { valueLabel: '', unitsLabel: '' }
    : formatUnits({ value: field.value, units: sourceUnits }, displayUnits);

  const handleFocus = useCallback(() => {
    if (disabled) {
      inputRef.current?.blur();
      return;
    }
    setIsEditing(true);
    seededDisplayRef.current = displayValue.valueLabel;
    setLocalValue(displayValue.valueLabel);
  }, [disabled, displayValue.valueLabel]);

  // While editing, store the raw typed value locally without conversion
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  // On blur, convert from display units to source units and persist to the form
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsEditing(false);
      const rawValue = e.target.value;
      if (rawValue === '') {
        if (field.value !== '') {
          field.onChange('');
        }
      } else if (rawValue !== seededDisplayRef.current) {
        const converted = transformUnits({ value: rawValue, units: displayUnits }, sourceUnits);
        const nextValue = converted.value == null ? '' : String(converted.value);
        if (nextValue !== field.value) {
          field.onChange(nextValue);
        }
      }
      field.onBlur();
    },
    [displayUnits, sourceUnits, field]
  );

  const blockCellClickEvent = useCallback(
    (e: MouseEvent<HTMLInputElement> | PointerEvent<HTMLInputElement>) => {
      if (!disabled) {
        return;
      }
      // Prevent focus and bubbling when interaction is blocked for this cell.
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled]
  );

  return (
    /* Deliberately NO `state="dirty"`, and the reason is the SIBLING cells, not this
       one. `dirtyCellTargets` is computed per {rowId, columnId} from form state and
       knows nothing about cell type (`use-dirty-cell-targets.ts`), while the same
       column renders three of them: this input, a `DropdownCell` (a button) and a
       Switch. Only this one could draw a registry border, so owning it here would
       give a dirty row two different borders from two systems — and with no registry
       answer for the other two before UXD-21, that split would be permanent.

       So the table keeps one uniform treatment through `DataTable.CellHighlight`
       (`equipment-bulk-edit-table.tsx:233`) and the field stays borderless,
       contributing only the value colour. Revisit when the dropdown and switch cells
       can carry their own state. */
    <Input className="w-full">
      <InputField
        ref={inputRef}
        className="border-none rounded-none"
        controlClassName={cn(
          'text-foreground text-sm',
          isDirty && 'text-ckds-base-caution',
          disabled && 'cursor-not-allowed'
        )}
        readOnly={disabled}
        tabIndex={disabled ? -1 : 0}
        value={isEditing ? localValue : displayValue.valueLabel}
        onBlur={handleBlur}
        onChange={handleChange}
        onClickCapture={blockCellClickEvent}
        onFocus={handleFocus}
        onPointerDownCapture={blockCellClickEvent}
      >
        <InputUnit className={cn(isDirty && 'text-ckds-base-caution')}>
          {isEditing ? getCatalogUnitsLabel(displayUnits) : displayValue.unitsLabel}
        </InputUnit>
      </InputField>
    </Input>
  );
};
